import {
  IAnalyticsConfiguration, IViewItemAnalyticsItem, IAnalyticsProvider, IViewItemAnalytics, IAddToCartAnalytics, IBeginCheckoutAnalytics,
  IRemoveFromCartAnalytics,
} from '../types';

interface IGA4Config {
  send_page_view: boolean;
  page_location: string;
  user_id?: number;
}

const defaultEventCallbackTimeout = 2000;

class GA4Provider implements IAnalyticsProvider {
  readonly isEnabled: boolean;

  readonly isGA4DatalayerEnabled: boolean;

  readonly userId: number;

  readonly measurementIds: string[];

  constructor({
    userId, isEnabled, measurementIds, isGA4DatalayerEnabled,
  }: IAnalyticsConfiguration) {
    this.isEnabled = isEnabled;
    this.isGA4DatalayerEnabled = isGA4DatalayerEnabled;
    this.userId = userId;
    this.measurementIds = measurementIds;
  }

  init(): void {
    const config: IGA4Config = {
      send_page_view: true,
      page_location: getPageLocationWithoutQueryString(),
    };

    if (this.userId > 0) {
      config.user_id = this.userId;
    }

    this.send('js', new Date());

    this.measurementIds.forEach((measurementId) => {
      if (!this.isEnabled) return;

      this.send('config', measurementId, config);
    });
  }

  trackPurchase(orderData: any): void {
    if (!this.isEnabled) return;

    this.send('event', 'purchase', orderData);
  }

  trackViewItem(viewItemData: IViewItemAnalytics): void {
    if (!this.isEnabled) return;

    this.send('event', 'view_item', viewItemData);
  }

  trackAddToCart(addToCartData: IAddToCartAnalytics): void {
    if (!this.isEnabled) return;

    this.send('event', 'add_to_cart', addToCartData);
  }

  pushEcommerceItems(items: IViewItemAnalyticsItem[]): void {
    if (!this.isGA4DatalayerEnabled) return;
    if (!items || items.length === 0) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ ecommerce: { items } });
  }

  trackBeginCheckout(beginCheckoutData: IBeginCheckoutAnalytics): void {
    if (!this.isEnabled) return;

    this.send('event', 'begin_checkout', beginCheckoutData);
  }

  trackRemoveFromCart(removeFromCartData: IRemoveFromCartAnalytics): Promise<void> {
    if (!this.isEnabled) return Promise.resolve();
    if (!removeFromCartData || removeFromCartData.items.length === 0) return Promise.resolve();

    return new Promise((resolve) => {
      this.send('event', 'remove_from_cart', {
        ...removeFromCartData,
        event_callback: resolve,
        event_timeout: defaultEventCallbackTimeout,
      });
    });
  }

  private send(...args: (any)[]) {
    if (!this.isEnabled) {
      return;
    }
    window.gtag(...args);
  }
}

function getPageLocationWithoutQueryString(): string {
  return `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
}

export default GA4Provider;
